
import { UserInput } from "../types";

/**
 * Sanitize user text input to prevent prompt injection and XSS
 */
export const sanitizeTextInput = (input: string, maxLength: number = 500): string => {
  // Strip any potential prompt injection patterns
  let sanitized = input
    .replace(/\n{3,}/g, '\n\n')           // Collapse excessive newlines
    .replace(/[<>]/g, '')                   // Remove HTML angle brackets
    .replace(/javascript:/gi, '')           // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '')            // Remove event handlers
    .replace(/\{[{%]/g, '')                // Remove template injection
    .replace(/[`]/g, "'")                  // Replace backticks
    .trim();

  // Truncate to max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Sanitize destination input specifically
 */
export const sanitizeDestination = (destination: string): string => {
  // Destinations should be simple place names
  return sanitizeTextInput(destination, 200)
    .replace(/[^\p{L}\p{N}\s,.\-']/gu, '') // Unicode letter support (Zürich, Köln, etc.)
    .trim();
};

/**
 * Sanitize all fields in UserInput
 */
export const sanitizeUserInput = (input: UserInput): UserInput => {
  return {
    ...input,
    destination: sanitizeDestination(input.destination || ''),
    clientName: sanitizeTextInput(input.clientName || '', 200),
    companionName: input.companionName ? sanitizeTextInput(input.companionName, 200) : input.companionName,
    homeAirport: sanitizeTextInput(input.homeAirport || '', 200),
    preferredHotel: input.preferredHotel ? sanitizeTextInput(input.preferredHotel, 300) : input.preferredHotel,
    auraDescription: input.auraDescription ? sanitizeTextInput(input.auraDescription, 1000) : input.auraDescription,
    // 2000 chars: agent notes can carry an appended portfolio-venue directive
    // (Concierge Studio sister venues) on top of free-text favorites
    agentFavorites: input.agentFavorites ? sanitizeTextInput(input.agentFavorites, 2000) : input.agentFavorites,
    // Agency / Business Card Fields
    agencyName: input.agencyName ? sanitizeTextInput(input.agencyName, 200) : input.agencyName,
    agentName: input.agentName ? sanitizeTextInput(input.agentName, 200) : input.agentName,
    agentTitle: input.agentTitle ? sanitizeTextInput(input.agentTitle, 200) : input.agentTitle,
    agentEmail: input.agentEmail ? sanitizeEmail(input.agentEmail) : input.agentEmail,
    agentPhone: input.agentPhone ? sanitizeTextInput(input.agentPhone, 30) : input.agentPhone,
    licenseNumber: input.licenseNumber ? sanitizeTextInput(input.licenseNumber, 100) : input.licenseNumber,
    contactInfo: input.contactInfo ? sanitizeTextInput(input.contactInfo, 500) : input.contactInfo,
  };
};

/**
 * Sanitize email input
 */
export const sanitizeEmail = (email: string): string => {
  return email.trim().toLowerCase().substring(0, 254);
};
